import { Base2Presenter } from "core/presenter";
import {
  CompanyRepository,
  PlanRepository,
  UserRepository
} from "core/repository";

import { fn } from "util";

class SupportCompanyPresenter extends Base2Presenter {
  initialState() {
    return {
      loading: false,
      filters: [],
      order: {
        field: "name",
        direction: "ASC"
      },
      pagination: {
        perPage: 25,
        currentPage: 1,
        total: 0
      },
      companies: [],
      checkedRows: {},
      anchor: null,
      plans: "",
      renderButton: false
    };
  }

  onStart() {
    this.getCompanies();
  }

  async getCompanies() {
    this.enqueue({ loading: true });
    const order = [this.viewModel.order];
    const pagination = this.viewModel.pagination;
    const filters = await this.getFilters();
    const repositoryResult = await CompanyRepository.getListOnline({
      order,
      pagination: {
        page: pagination.currentPage,
        perPage: pagination.perPage
      },
      filters
    });

    const plansResult = await PlanRepository.getAll({});
    if (plansResult.success) {
      const plans = plansResult.data || [];
      this.enqueue({ plans });
    } else {
      this.sendNotification({
        type: "error",
        title: "Ooops...",
        content:
          "Ocorreu um erro. Não foi possível carregar os planos das empresas. Tente novamente mais tarde."
      });
    }

    if (repositoryResult.success) {
      this.enqueue(vm => ({
        companies: repositoryResult.data,
        pagination: {
          ...vm.pagination,
          total: repositoryResult.total,
          perPage: repositoryResult.perPage
        }
      }));
    } else {
      this.sendNotification({
        type: "error",
        title: "Ooops...",
        content:
          "Ocorreu um erro. Não foi possível carregar os dados. Tente novamente mais tarde."
      });
    }

    this.enqueue({ checkedRows: {}, loading: false });
  }

  onPageChange(page) {
    this.enqueue(
      vm => ({
        pagination: {
          ...vm.pagination,
          currentPage: page
        }
      }),
      () => {
        this.viewModel.filters.email
          ? this.filterByUserEmail()
          : this.getCompanies();
      }
    );
  }

  onOrderChange(field, direction) {
    this.enqueue(
      vm => ({
        order: {
          ...vm.order,
          field,
          direction
        }
      }),
      () => this.getCompanies()
    );
  }

  onCheckBoxEnqueue(checkedRows) {
    this.enqueue({ checkedRows }, () =>
      this.checkSelectedCheckboxes(this.viewModel.checkedRows)
    );
  }

  onSingleCheckBoxEnqueue(checkedRows) {
    this.enqueue(
      vm => ({
        checkedRows: {
          ...vm.checkedRows,
          ...checkedRows
        }
      }),
      () => this.checkSelectedCheckboxes(this.viewModel.checkedRows)
    );
  }

  checkSelectedCheckboxes(checkboxes) {
    const checkSelectedCheckboxes = Object.values(checkboxes);

    if (checkSelectedCheckboxes.includes(true)) {
      this.enqueue({ renderButton: true });
    } else {
      this.enqueue({ renderButton: false });
    }
  }

  parseCheckedData(bool) {
    const companies = [];
    const cr = this.viewModel.checkedRows;
    const oldData = Object.keys(cr).map(key => {
      if (cr[key]) {
        return [key, cr[key]];
      }
      return [];
    });

    oldData.forEach(od => {
      if (od.length > 0) {
        companies.push(od[0]);
      }
    });

    return { companies, action: bool ? "block" : "unblock" };
  }

  async getFilters() {
    const filters = this.viewModel.filters;
    return filters;
  }

  async onFilterChange(fields) {
    const filters = [];

    if (!fn.isBlank(fields.cnpj)) {
      filters.push({
        connector: "AND",
        field: "fiscal_informations.cnpj",
        operation: "LIKE",
        value: fields.cnpj.replace(/\D/g, "")
      });
    }
    if (!fn.isBlank(fields.email) && fields.email.length >= 3) {
      this.enqueue(
        vm => ({
          pagination: {
            ...vm.pagination,
            currentPage: 1
          },
          filters: {
            ...vm.filters,
            email: fields.email
          }
        }),
        async () => await this.filterByUserEmail()
      );
    }
    if (!fn.isBlank(fields.legal_name)) {
      filters.push({
        connector: "AND",
        field: "fiscal_informations.legal_name",
        operation: "LIKE",
        value: fields.legal_name
      });
    }
    if (!fn.isBlank(fields.name)) {
      filters.push({
        connector: "AND",
        field: "name",
        operation: "LIKE",
        value: fields.name
      });
      filters.push({
        connector: "OR",
        field: "fiscal_informations.legal_name",
        operation: "LIKE",
        value: fields.name
      });
    }
    if (!fn.isBlank(fields.plan)) {
      if (fields.plan !== "all") {
        filters.push({
          connector: "AND",
          field: "plan_uuid",
          operation: "EQ",
          value: fields.plan
        });
      }
    }
    if (!fn.isBlank(fields.status)) {
      if (fields.status !== "all") {
        filters.push({
          connector: "AND",
          field: "blocked",
          operation: "EQ",
          value: fields.status
        });
      }
    }

    if (fn.isBlank(fields.email)) {
      this.enqueue(
        vm => ({
          filters,
          pagination: {
            ...vm.pagination,
            currentPage: 1
          }
        }),
        () => this.getCompanies()
      );
    }
  }

  async filterByUserEmail() {
    this.enqueue({ loading: true });
    const { email } = this.viewModel.filters;
    const result = await UserRepository.getSupportUsers({
      filters: [{ field: "username", operation: "LIKE", value: email }]
    });

    if (result.success) {
      const allAllowedCompanies = this.findAllAllowedCompanies(result.data);
      const allFilters = this.getAllCompaniesFilters(allAllowedCompanies);
      const companyAmount = allAllowedCompanies.length;
      const { pagination } = this.viewModel;
      const { perPage, currentPage } = pagination;

      const companyResult = await CompanyRepository.getListOnline({
        pagination: {
          page: currentPage,
          perPage
        },
        filters: allFilters
      });

      if (companyResult.success) {
        this.enqueue({
          companies: companyResult.data,
          pagination: {
            perPage: 25,
            currentPage: currentPage || 1,
            total: companyAmount
          }
        });
      } else {
        this.sendNotification({
          type: "error",
          title: "Oops...",
          content:
            "Não foi possível buscar as empresas do email informado. Tente novamente mais tarde."
        });
      }
    } else {
      this.sendNotification({
        type: "error",
        title: "Oops...",
        content:
          "Não foi possível buscar por email. Tente novamente mais tarde."
      });
    }
    this.enqueue({ loading: false });
  }

  findAllAllowedCompanies(usersFound) {
    const allAllowedCompanies = [];
    usersFound.forEach(user => {
      if (!allAllowedCompanies.includes(user.company_uuid)) {
        allAllowedCompanies.push(user.company_uuid);
      }
    });
    return allAllowedCompanies;
  }

  getAllCompaniesFilters(allAllowedCompanies) {
    const allFilters = [];
    let tempUuid = "";
    for (let i = 0; i < allAllowedCompanies.length; i++) {
      tempUuid = allAllowedCompanies[i];
      allFilters.push({
        field: "uuid",
        operation: "LIKE",
        value: tempUuid,
        connector: "OR"
      });
    }
    return allFilters;
  }

  handleMenuOpen(event) {
    this.enqueue({ anchor: event.currentTarget });
  }

  handleMenuClose() {
    this.enqueue({ anchor: null });
  }

  handleMenuClick(bool) {
    const mensagem = bool ? "bloquear" : "desbloquear";
    const checkedCompanies = this.parseCheckedData(bool);

    if (checkedCompanies.length === 0) {
      this.sendNotification({
        type: "error",
        title: "Oops...",
        content: `Selecione no mínimo uma empresa para ${mensagem}.`
      });
    } else {
      this.sendNotification({
        type: "confirmation",
        title: "Você tem certeza?",
        content: `Tem certeza que deseja ${mensagem} todas as empresas selecionadas?`,
        actions: [
          {
            label: "Voltar"
          },
          {
            label: "Sim, eu tenho certeza",
            type: "accent",
            callback: () => this.patchCheckedCompanies(bool, checkedCompanies)
          }
        ]
      });
    }

    this.enqueue({ anchor: null });
  }

  async patchCheckedCompanies(bool, checkedCompanies) {
    this.enqueue({ loading: true });
    const result = await CompanyRepository.toggleBlockCompanies(
      checkedCompanies
    );
    const mensagemSucesso = bool ? "bloqueadas" : "desbloqueadas";
    const mensagemErro = bool ? "bloquear" : "desbloquear";

    this.enqueue({ loading: false });

    if (result.success) {
      this.sendNotification({
        type: "success",
        content: `As empresas foram ${mensagemSucesso} com sucesso!`
      });
      const filters = await this.getFilters();
      if (fn.isBlank(filters.email)) {
        await this.getCompanies();
      } else {
        await this.filterByUserEmail();
      }
    } else if (result.fatal) {
      this.sendNotification({
        type: "error",
        title: "Oops...",
        content: `Não foi possível atualizar a empresas. Tente mais tarde ou contate o suporte.`
      });
    } else {
      this.sendNotification({
        type: "error",
        title: "Oops...",
        content: `Não foi possível ${mensagemErro} as empresas. Tente mais tarde ou contate o suporte.`
      });
    }
  }

  companyDetails(uuid) {
    this.navigateTo("app.support.company.show", { companyUuid: uuid });
  }
}

export default SupportCompanyPresenter;



// WEBPACK FOOTER //
// ./src/components/app/support/company/presenter.js